import { z } from 'zod';
import type { AudienceDefinition, AudienceReach } from './audience.ts';

/** Saved audiences (BB-46). The definition is checked like `audience/describe`; an invalid one is a 400. */
export const SEGMENT_STATUSES = ['active', 'archived'] as const;

const Name = z.string().trim().min(1).max(200);
const Description = z.string().trim().max(1000).nullable();

export const SegmentListQuery = z.object({
  query: z.string().trim().max(200).optional(),
  tagId: z.uuid().optional(),
  starred: z.enum(['true', 'false']).optional(),
  status: z.enum([...SEGMENT_STATUSES, 'all']).default('active'),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type SegmentListQuery = z.infer<typeof SegmentListQuery>;

export const CreateSegmentBody = z.strictObject({ name: Name, description: Description.optional(), definition: z.unknown() });
export type CreateSegmentBody = z.infer<typeof CreateSegmentBody>;

/** Changes against `revision`: a segment changed by someone else in the meantime answers `412 revision_conflict`. */
export const UpdateSegmentBody = z
  .strictObject({ revision: z.number().int().min(1), name: Name.optional(), description: Description.optional(), definition: z.unknown().optional() })
  .refine((b) => b.name !== undefined || b.description !== undefined || b.definition !== undefined, 'nothing to change');
export type UpdateSegmentBody = z.infer<typeof UpdateSegmentBody>;

export interface SegmentPerson {
  id: string;
  email: string | null;
}

export interface SegmentSummary {
  id: string;
  apiId: string;
  name: string;
  description: string | null;
  status: (typeof SEGMENT_STATUSES)[number];
  revision: number;
  starred: boolean;
  tags: Array<{ id: string; name: string; color: string | null }>;
  /** The latest reach result cached for the current definition (BB-44); `null` until someone looks at its size. */
  size: { count: number; mode: AudienceReach['mode']; computedAt: string } | null;
  appsMode: AudienceDefinition['apps']['mode'];
  createdBy: SegmentPerson | null;
  updatedBy: SegmentPerson | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface SegmentView extends SegmentSummary {
  definition: AudienceDefinition;
}

export interface SegmentRevisionView {
  revision: number;
  name: string;
  description: string | null;
  definition: AudienceDefinition;
  /** Sentences of the definition (`locale` of the request). */
  sentences: string[];
  /** What this revision changed in the sentences against the one before it (all `added` for the first). */
  changes: Array<{ kind: 'same' | 'added' | 'removed'; sentence: string }>;
  createdBy: SegmentPerson | null;
  createdAt: string;
}

/** «Create campaign»: the audience a campaign draft starts from and where its wizard opens (BB-97). */
export interface SegmentCampaignDraft {
  segmentId: string;
  audience: AudienceDefinition;
  wizardUrl: string;
  /** The campaign wizard exists in this build. */
  available: boolean;
}

/** An object that uses another one (where-used, BB-47). */
export interface DependentView {
  objectType: string;
  objectId: string;
  name: string;
  /** How it refers: `audience`, `exclusion`, `attribute_ref`, `event_ref`, or a setting path. */
  via: string;
  status?: 'active' | 'scheduled' | 'paused' | 'draft' | 'inactive';
  url?: string;
}

/** Archive answer: drafts that use the segment do not block it but are listed. */
export interface SegmentArchiveResult extends SegmentView {
  warnings: DependentView[];
}

/** `GET …/objects/:type/:id/where-used`: dependents grouped by type. */
export interface WhereUsed {
  objectType: string;
  objectId: string;
  groups: Array<{ objectType: string; items: DependentView[] }>;
}

/** Daily size of a segment (BB-48). */
export interface SegmentSizePoint {
  day: string;
  mode: 'estimate' | 'exact';
  count: number;
  low: number;
  high: number;
}

export const SegmentHistoryQuery = z.object({ days: z.coerce.number().int().refine((d) => d === 30 || d === 90, 'days must be 30 or 90').default(30) });
export const MAX_COMPARED_SEGMENTS = 5;
export const SegmentInsightsBody = z.strictObject({ segmentIds: z.array(z.uuid()).min(1).max(MAX_COMPARED_SEGMENTS) });

export interface KpiValue {
  mean: number | null;
  median: number | null;
}
export interface SessionKpisView {
  profiles: number;
  sessionsPerDay: KpiValue;
  daysSinceFirstSession: KpiValue;
  daysSinceLastSession: KpiValue;
}
export interface SegmentInsights {
  /** Every active profile of the environment. */
  baseline: SessionKpisView;
  segments: Array<{ segmentId: string; name: string; kpis: SessionKpisView }>;
  /** KPIs come from a stable 10 % sample in environments of 50 000 active profiles and more. */
  sampled: boolean;
  computedAt: string;
}
